import type {
  MinimumBetSchema,
  MissionProgressSchema,
  MissionSchema,
  PrizeSchema,
  ProgressSchema,
  PublicCampaignSchema,
  TaskSchema,
} from "@/types";
import {
  formatPrizeTitle,
  parseDisplayNumber,
  topPrizeByTitle,
  type TranslateFn,
} from "@/utils/helpers";

/**
 * The rendered Missions view objects the components consume.
 *
 * NOT a wire schema — assembled (see `./assemble`) from two streams:
 * `campaigns` (structural `PublicCampaignSchema.missions`: tasks + prize titles,
 * cached hard, pushed once) ⋈ `mission_progress` (per-player volatile state,
 * ~1s cadence, deliberately carries NO prizes). Fields are composed from the
 * generated schemas so a backend change surfaces here; only the shaping the
 * components need (a React-key `id`, folded per-player flags) is added on top.
 */

/** One task — structural fields folded with the player's live state. */
export interface MissionTask
  extends Pick<TaskSchema, "order" | "kind" | "title"> {
  /** Synthetic stable row id (content-derived), for React keys. */
  id: string;
  /** Per-task prizes from the structural stream (empty = none). */
  prizes: PrizeSchema[];
  /** Live per-player flags — false until the progress stream lands. */
  isActive: boolean;
  isCompleted: boolean;
  progress: ProgressSchema | null;
}

export interface Mission {
  id: MissionSchema["id"];
  /** 1-based position in the campaign's ordered ladder. */
  position: number;
  /** Mission-completion prizes (titles rendered by the backend). */
  prizes: PrizeSchema[];
  /** Tasks must be done in their order (one active at a time); else any order. */
  keepOrder: boolean;
  tasks: MissionTask[];
  /** Live per-player flags — false until the progress stream lands. */
  isActive: boolean;
  isCompleted: boolean;
  /** Mission repetition counter (`current` completions of `total` rounds). */
  progress: ProgressSchema | null;
}

export interface MissionsCampaign {
  campaign: PublicCampaignSchema;
  minimum_bet: Pick<MinimumBetSchema, "value" | "currency"> &
    Partial<Pick<MinimumBetSchema, "display">>;
  missions: Mission[];
  eligible_games: string[] | "ALL";
  /** Total eligible-games count (list may be a preview subset). */
  eligible_games_count?: number;
}

/** Sidebar "MISSIONS COMPLETED" numerator. */
export const completedMissionCount = (mc: MissionsCampaign): number =>
  mc.missions.filter((m) => m.isCompleted).length;

/** Player finished everything (drives the celebration banner). The backend
 *  sends no explicit campaign-completed flag on the widget streams, so it is
 *  inferred from every mission being completed. */
export const allMissionsCompleted = (mc: MissionsCampaign): boolean =>
  mc.missions.length > 0 && mc.missions.every((m) => m.isCompleted);

/** The mission the player should act on: the backend marks exactly the first
 *  unfinished mission `is_active`; before the progress stream lands (or when
 *  logged out) fall back to the first uncompleted one so the ladder still has
 *  a "current" rung. */
export const currentMission = (mc: MissionsCampaign): Mission | undefined =>
  mc.missions.find((m) => m.isActive) ??
  mc.missions.find((m) => !m.isCompleted);

/** `number_of_repetitions > 0` reaches the FE only as `progress.total > 1`. */
export const isRepeatable = (m: Mission): boolean =>
  parseProgressValue(m.progress?.total) !== null &&
  parseProgressValue(m.progress?.total)! > 1;

/**
 * Progress values are backend-rendered strings; the bar/round math needs
 * numbers. Today's mapper emits bare dot-decimal Decimals ("0.00", "500") and
 * plain int counts, but parse defensively: the format is the backend's to
 * change (locale separators, currency symbols) and serialized Decimals can go
 * scientific ("1E-7") on crypto sub-currencies. Best-effort; null when it
 * doesn't look numeric.
 */
export function parseProgressValue(
  raw: string | null | undefined
): number | null {
  return parseDisplayNumber(raw);
}

/**
 * Display form of a wire progress value. The mapper serializes raw Decimals,
 * which on 8-decimal crypto currencies keep full precision ("10.00000000") and
 * go scientific for tiny values ("0E-8") — normalize to a locale-formatted
 * number for display. Verbatim fallback when it doesn't parse, so a backend
 * that starts sending pre-formatted labels still renders.
 */
export function formatProgressValue(raw: string): string {
  const parsed = parseProgressValue(raw);
  if (parsed === null) return raw;
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 8,
  }).format(parsed);
}

/**
 * One-shot tasks (total = 1, e.g. "Make a deposit") carry no meaningful
 * count — "0/1" is noise, so all progress UI is suppressed for them; the
 * row/pill completion state alone says done or not.
 */
export function isCountableProgress(progress: ProgressSchema | null): boolean {
  const total = parseProgressValue(progress?.total);
  return total !== null && total > 1;
}

/** 0..1 bar fill ratio, or null when the strings don't parse. */
export function progressRatio(progress: ProgressSchema | null): number | null {
  if (!progress) return null;
  const current = parseProgressValue(progress.current);
  const total = parseProgressValue(progress.total);
  if (current === null || total === null || total <= 0) return null;
  return Math.min(1, Math.max(0, current / total));
}

/** The single prize the reward line shows: the most valued of the MISSION's
 *  own prizes (see `topPrizeByTitle` for the ranking). Task-prize-only
 *  missions have none — they summarize as a count (`missionRewardLabel`). */
export const missionRewardPrize = (m: Mission): PrizeSchema | null =>
  topPrizeByTitle(m.prizes);

/** How many mission prizes the line does NOT show — rendered as a "+N Prizes"
 *  badge next to the games line rather than a bare "+N" glued to the title. */
export const missionRewardExtraCount = (m: Mission): number =>
  Math.max(0, m.prizes.length - 1);

/** Total prizes paid on the mission's tasks (0 when none configured). */
export const missionTaskPrizeCount = (m: Mission): number =>
  m.tasks.reduce((n, task) => n + task.prizes.length, 0);

/** "25 Free Spins" — the most valued mission prize, display-formatted. A
 *  mission configured with task-level prizes only summarizes them as a count
 *  ("3 Prizes") instead of promoting one task's prize to headline status, so
 *  the rung never renders rewardless while it actually pays something. */
export const missionRewardLabel = (
  m: Mission,
  t: TranslateFn
): string | null => {
  const prize = missionRewardPrize(m);
  if (prize) return formatPrizeTitle(prize.title, t);
  const count = missionTaskPrizeCount(m);
  if (count === 0) return null;
  // Reuses the MorePrizesBadge nouns so no new strings need translating.
  return `${count} ${
    count === 1
      ? t("igwButton.prize", "Prize")
      : t("igwButton.prizes", "Prizes")
  }`;
};

/** Games of the SHOWN reward prize (free spins) — scoped to the one prize the
 *  line displays, not all reward prizes: a games line from a hidden prize
 *  would read as if the shown reward plays on that game. Empty for cash/bonus
 *  rewards; the "+N Prizes" badge stands in for the rest. */
export const missionRewardGames = (m: Mission): string[] => {
  const seen = new Set<string>();
  const games: string[] = [];
  for (const game of missionRewardPrize(m)?.games ?? []) {
    if (!seen.has(game)) {
      seen.add(game);
      games.push(game);
    }
  }
  return games;
};

/**
 * The campaign's top prize for the home card: highest money value across ALL
 * missions' mission-level AND task-level prizes ("Top prize", mirroring the
 * tournament card — but value-ranked, since mission prizes carry no place).
 * Money values can't be compared to spin counts, so ranking is tiered: any
 * priced prize beats any free-spins one; within a tier the amount/count wins.
 * When neither is rankable, falls back to the first prize in ladder order.
 */
export const missionsTopPrize = (mc: MissionsCampaign): PrizeSchema | null =>
  topPrizeByTitle(
    mc.missions.flatMap((m) => [
      ...m.prizes,
      ...m.tasks.flatMap((task) => task.prizes),
    ])
  );

/** Re-export for the store slice: raw progress page grouped per campaign. */
export type MissionProgressByCampaign = Record<string, MissionProgressSchema[]>;
